import { Form, Input, InputNumber, Select, Switch } from 'antd';
import { WorkflowVariableAutocompleteInput } from './WorkflowVariableAutocompleteInput';
import type {
  WorkflowActionSchema,
  WorkflowDecisionSchema,
} from './WorkflowTemplateBuilder.types';

type DynamicFieldProps = {
  fieldPath: (string | number)[];
  inputName: string;
  inputSchema: {
    type?: string;
    required?: boolean;
    description?: string;
  };
  formItemLayout?: {
    labelCol?: { flex?: string };
    wrapperCol?: { flex?: string };
  };
  variableOptions?: Array<{ value: string }>;
  workflowActionSchemas?: Record<string, WorkflowActionSchema>;
  workflowDecisionSchemas?: Record<string, WorkflowDecisionSchema>;
};

export const WorkflowTemplateDynamicField = ({
  fieldPath,
  inputName,
  inputSchema,
  variableOptions,
  workflowActionSchemas,
  workflowDecisionSchemas,
}: DynamicFieldProps) => {
  const inputType = inputSchema.type || 'string';
  const isRequired = Boolean(inputSchema.required);
  const rowLabel = (
    <span className="text-xs text-[#595959]">
      {isRequired ? <span className="text-[#ff4d4f] mr-1">*</span> : null}
      {inputName}
    </span>
  );

  if (inputType === 'boolean') {
    return (
      <div className="flex items-center gap-2">
        <div className="w-24 flex-none">{rowLabel}</div>
        <Form.Item
          name={[...fieldPath, inputName]}
          valuePropName="checked"
          tooltip={inputSchema.description}
          className="!mb-0 flex-1"
        >
          <Switch />
        </Form.Item>
      </div>
    );
  }

  if (inputType === 'integer' || inputType === 'number') {
    return (
      <div className="flex items-center gap-2">
        <div className="w-24 flex-none">{rowLabel}</div>
        <Form.Item
          name={[...fieldPath, inputName]}
          rules={isRequired ? [{ required: true, message: 'Required' }] : []}
          tooltip={inputSchema.description}
          className="!mb-0 flex-1"
        >
          <InputNumber size="small" className="w-full" />
        </Form.Item>
      </div>
    );
  }

  if (inputType === 'array') {
    return (
      <div className="flex items-center gap-2">
        <div className="w-24 flex-none">{rowLabel}</div>
        <Form.Item
          name={[...fieldPath, inputName]}
          rules={isRequired ? [{ required: true, message: 'Required' }] : []}
          tooltip={inputSchema.description}
          className="!mb-0 flex-1"
        >
          <Select
            size="small"
            mode="tags"
            tokenSeparators={[',']}
            placeholder="Enter values"
          />
        </Form.Item>
      </div>
    );
  }

  if (inputType === 'object') {
    return (
      <div className="flex items-center gap-2">
        <div className="w-24 flex-none">{rowLabel}</div>
        <Form.Item
          name={[...fieldPath, inputName]}
          rules={isRequired ? [{ required: true, message: 'Required' }] : []}
          tooltip={inputSchema.description}
          className="!mb-0 flex-1"
        >
          <Input.TextArea
            rows={2}
            placeholder="Enter object-like value for this input"
          />
        </Form.Item>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <div className="w-24 flex-none">{rowLabel}</div>
      <Form.Item
        name={[...fieldPath, inputName]}
        rules={isRequired ? [{ required: true, message: 'Required' }] : []}
        tooltip={inputSchema.description}
        className="!mb-0 flex-1"
      >
        <WorkflowVariableAutocompleteInput
          size="small"
          additionalOptions={(variableOptions || []).map((opt) => opt.value)}
          workflowActionSchemas={workflowActionSchemas}
          workflowDecisionSchemas={workflowDecisionSchemas}
        />
      </Form.Item>
    </div>
  );
};
